import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { services } from "../../globals";
import { FILE_EXISTS_PATH_BATCH_LIMIT } from "../../shared/utils/file-exists.utils";
import { evaluateBooleanExpression } from "../../shared/utils/expression.utils";
import { useAuth } from "../internal/AuthProvider";
import { useBookOutletScope } from "../../state/BookOutletScopeContext";
import { useFilterState } from "../../state/FilterContext";
import {
    LinkVerifierContext,
    type LinkVerifierContextValue,
    type LinkVerifierStatus,
} from "../../state/link-verifier.context";
import { useSiteContext } from "../../state/SiteContext";
import { Site } from "../../shared/models/site.model";
import { FilterState } from "../../state/state-models";
import { applyDefaults } from "../../state/state-url-management";
import { useVerifiedLinkSequencePublisher } from "../../state/verified-link-sequence.context";
import { findFirstComponentOfType, TextExtractor } from "../internal/TextExtractor";

const EMPTY_RESULTS = new Map<string, boolean>();

type EnablePhase = "always" | "unresolved" | "on" | "off";

interface LinkVerifierProps {
    children: React.ReactNode;
}

function appliedValidityAndLang(
    validity: string | undefined,
    lang: string | undefined,
    site: Site | null | undefined,
): { validity: string; lang: string } {
    const copy: FilterState = { validity, lang };
    if (site) {
        applyDefaults(copy, site);
    }
    return {
        validity: copy.validity || "",
        lang: copy.lang || "",
    };
}

function authorizedUserKey(user: { id?: unknown } | null, isAuthenticated: boolean): string {
    if (!isAuthenticated || user == null) {
        return "";
    }
    const id = user.id;
    if (id == null || id === "" || id === "guest") {
        return "";
    }
    return String(id);
}

function buildFilterKey(siteName: string, validity: string, lang: string, userId: string): string {
    return `${siteName}\u001f${validity}\u001f${lang}\u001f${userId}`;
}

function chunkPaths(paths: string[]): string[][] {
    const chunks: string[][] = [];
    for (let index = 0; index < paths.length; index += FILE_EXISTS_PATH_BATCH_LIMIT) {
        chunks.push(paths.slice(index, index + FILE_EXISTS_PATH_BATCH_LIMIT));
    }
    return chunks;
}

type RegisteredInstance = {
    path: string;
    order: number;
};

function uniquePathsFromInstances(instances: Map<string, RegisteredInstance>): string[] {
    const ordered = [...instances.values()].sort((left, right) => left.order - right.order);
    const seen = new Set<string>();
    const paths: string[] = [];
    for (const instance of ordered) {
        if (!seen.has(instance.path)) {
            seen.add(instance.path);
            paths.push(instance.path);
        }
    }
    return paths;
}

function derivePublication(
    instances: Map<string, RegisteredInstance>,
    results: Map<string, boolean>,
): { pending: boolean; okPaths: string[] } {
    const ordered = [...instances.values()].sort((left, right) => left.order - right.order);
    let pending = false;
    const okPaths: string[] = [];
    for (const instance of ordered) {
        if (!results.has(instance.path)) {
            pending = true;
            continue;
        }
        if (results.get(instance.path)) {
            okPaths.push(instance.path);
        }
    }
    return { pending, okPaths };
}

function isArgEnableElement(child: React.ReactNode): child is React.ReactElement {
    return React.isValidElement(child) && child.type.toString().toLowerCase() === "arg-enable";
}

function deriveEnablePhase(hasSlot: boolean, extractedText: string | null): EnablePhase {
    if (!hasSlot) {
        return "always";
    }
    if (extractedText === null) {
        return "unresolved";
    }
    return evaluateBooleanExpression(extractedText) ? "on" : "off";
}

function verificationIsEnabled(phase: EnablePhase): boolean {
    return phase === "always" || phase === "on";
}

/**
 * Provider-only user-function: verifies descendant local `AppLink`s and stamps
 * `_pending_verification` / `_broken_link`. No extra DOM node around the region.
 * Optional `arg-enable` (If-style expression) gates verification; remaining children
 * are the verified region.
 */
export function LinkVerifier({ children }: LinkVerifierProps): JSX.Element {
    const filterState = useFilterState();
    const { siteState } = useSiteContext();
    const { inBookOutlet } = useBookOutletScope();
    const { registerVerifier, updateVerifier, unregisterVerifier } = useVerifiedLinkSequencePublisher();
    const siteName = siteState.site?.name || "";
    const { validity, lang } = useMemo(
        () => appliedValidityAndLang(filterState.validity, filterState.lang, siteState.site),
        [filterState.validity, filterState.lang, siteState.site],
    );
    const { user, isAuthenticated, isLoading } = useAuth();
    const liveAuthKey = authorizedUserKey(user, isAuthenticated);
    // The first session check has no settled user yet, so existence waits.
    // A later reload (window focus) keeps the last user until that check finishes.
    const settledAuthKeyRef = useRef<string | null>(null);
    if (!isLoading) {
        settledAuthKeyRef.current = liveAuthKey;
    }
    const authKey = isLoading ? settledAuthKeyRef.current : liveAuthKey;
    const filterKey = authKey === null
        ? null
        : buildFilterKey(siteName, validity, lang, authKey);

    const enableSlot = useMemo(() => findFirstComponentOfType(children, "arg-enable"), [children]);
    const regionChildren = useMemo(
        () => React.Children.toArray(children).filter((child) => !isArgEnableElement(child)),
        [children],
    );
    const [extractedText, setExtractedText] = useState<string | null>(null);
    const enablePhase = deriveEnablePhase(enableSlot != null, extractedText);
    const enabled = verificationIsEnabled(enablePhase);
    const isOff = enablePhase === "off";

    const [cache, setCache] = useState<{ key: string; results: Map<string, boolean> }>(() => ({
        key: filterKey ?? "",
        results: new Map(),
    }));

    const results = filterKey !== null && cache.key === filterKey ? cache.results : EMPTY_RESULTS;

    const instancesRef = useRef<Map<string, RegisteredInstance>>(new Map());
    const nextOrderRef = useRef(0);
    const mountSeqRef = useRef<number | null>(null);
    const resultsRef = useRef<Map<string, boolean>>(results);
    const requestRef = useRef({ siteName, validity, lang, filterKey: filterKey ?? "" });
    const authSettledRef = useRef(authKey !== null);
    const generationRef = useRef(0);
    const rafRef = useRef<number | null>(null);
    const runBatchRef = useRef<() => Promise<void>>(async () => undefined);
    const enablePhaseRef = useRef(enablePhase);
    const [instanceVersion, setInstanceVersion] = useState(0);

    resultsRef.current = results;
    requestRef.current = { siteName, validity, lang, filterKey: filterKey ?? "" };
    authSettledRef.current = authKey !== null;
    enablePhaseRef.current = enablePhase;

    const scheduleBatch = useCallback(() => {
        if (typeof window === "undefined") {
            return;
        }
        if (!authSettledRef.current) {
            return;
        }
        if (!verificationIsEnabled(enablePhaseRef.current)) {
            return;
        }
        if (rafRef.current != null) {
            return;
        }
        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            void runBatchRef.current();
        });
    }, []);

    runBatchRef.current = async () => {
        if (!authSettledRef.current || !verificationIsEnabled(enablePhaseRef.current)) {
            return;
        }
        const generation = generationRef.current;
        const { siteName: site, validity: requestValidity, lang: requestLang, filterKey: requestKey } = requestRef.current;
        if (!requestKey) {
            return;
        }
        const cached = resultsRef.current;
        const needed = uniquePathsFromInstances(instancesRef.current).filter((path) => !cached.has(path));
        if (needed.length === 0) {
            return;
        }

        const markResolved = (existingSet: Set<string> | null): void => {
            if (generationRef.current !== generation || requestRef.current.filterKey !== requestKey) {
                return;
            }
            const next = new Map(resultsRef.current);
            for (const path of needed) {
                next.set(path, existingSet === null ? true : existingSet.has(path));
            }
            resultsRef.current = next;
            setCache({ key: requestKey, results: next });
            const leftovers = uniquePathsFromInstances(instancesRef.current).filter((path) => !next.has(path));
            if (leftovers.length > 0) {
                scheduleBatch();
            }
        };

        if (!site) {
            markResolved(null);
            return;
        }

        try {
            const parts = await Promise.all(
                chunkPaths(needed).map((chunk) =>
                    services.file.existsByPaths(site, chunk, requestValidity, requestLang),
                ),
            );
            if (generationRef.current !== generation || requestRef.current.filterKey !== requestKey) {
                return;
            }
            markResolved(new Set(parts.flat()));
        } catch {
            markResolved(null);
        }
    };

    useEffect(() => {
        if (!filterKey) {
            return;
        }
        generationRef.current += 1;
        if (enabled) {
            scheduleBatch();
        }
        return () => {
            if (rafRef.current != null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [enabled, filterKey, scheduleBatch]);

    const register = useCallback((instanceId: string, path: string) => {
        const existing = instancesRef.current.get(instanceId);
        if (existing) {
            if (existing.path === path) {
                return;
            }
            existing.path = path;
        } else {
            instancesRef.current.set(instanceId, { path, order: nextOrderRef.current });
            nextOrderRef.current += 1;
        }
        setInstanceVersion((version) => version + 1);
        scheduleBatch();
    }, [scheduleBatch]);

    const unregister = useCallback((instanceId: string) => {
        if (!instancesRef.current.delete(instanceId)) {
            return;
        }
        setInstanceVersion((version) => version + 1);
    }, []);

    const getStatus = useCallback((path: string): LinkVerifierStatus => {
        if (enablePhase === "unresolved") {
            return "pending";
        }
        if (enablePhase === "off") {
            return "off";
        }
        if (results.has(path)) {
            return results.get(path) ? "ok" : "broken";
        }
        return "pending";
    }, [enablePhase, results]);

    const value = useMemo((): LinkVerifierContextValue => ({
        register,
        unregister,
        getStatus,
    }), [getStatus, register, unregister]);

    useEffect(() => {
        if (isOff) {
            return;
        }
        const mountSeq = registerVerifier();
        mountSeqRef.current = mountSeq;
        return () => {
            unregisterVerifier(mountSeq);
            if (mountSeqRef.current === mountSeq) {
                mountSeqRef.current = null;
            }
        };
    }, [isOff, registerVerifier, unregisterVerifier]);

    useEffect(() => {
        const mountSeq = mountSeqRef.current;
        if (mountSeq == null || enablePhase === "off") {
            return;
        }
        if (enablePhase === "unresolved") {
            updateVerifier(mountSeq, { inBookOutlet, pending: true, okPaths: [] });
            return;
        }
        const { pending, okPaths } = derivePublication(instancesRef.current, results);
        updateVerifier(mountSeq, { inBookOutlet, pending, okPaths });
    }, [enablePhase, inBookOutlet, instanceVersion, results, updateVerifier]);

    return (
        <LinkVerifierContext.Provider value={value}>
            {enableSlot && (
                <TextExtractor onExtractedText={setExtractedText}>
                    {enableSlot.props.children}
                </TextExtractor>
            )}
            {regionChildren}
        </LinkVerifierContext.Provider>
    );
}
